import {AxiosResponse} from 'axios';
import letrusAPI from 'APIs/letrusAPI/axiosInstance';
import {SimplifiedFeedbackData} from './types';

// Real contract confirmed live against the BO-2300 branch (letrus-api, Gustavo, 2026-09-21):
// `GET /api/v1/compositions/{compositionId}/simplified_feedback_grades/` returns
// `{criteria_grades: {c2,c4,c6}, normalized_score, level: {id,label,stars}}` — snake_case,
// different from the `SimplifiedFeedbackData` (camelCase) that the rest of the front already
// consumes, so the translation happens here, at the single entry point for the real data.
// Empty body (`{}`, 200) when the review hasn't finished yet — the front treats it as "no data
// yet" (same state as loading), not as an error.
// Typed as a real Partial (not the final shape) — the value below is only promoted to the
// strong SimplifiedFeedbackData type after `isCompleteGradesPayload` confirms at runtime that
// the three criteria and the level came filled in (code review finding, 2026-09-21: checking
// only the object's truthiness let `criteria_grades: {}`/partial through as if it were valid
// data).
interface SimplifiedFeedbackGradesResponse {
  criteria_grades?: Partial<SimplifiedFeedbackData['criteriaGrades']>;
  level?: Partial<{id: string; label: string; stars: number}>;
}

interface CompleteGradesPayload {
  criteria_grades: SimplifiedFeedbackData['criteriaGrades'];
  level: {id?: string; label: string; stars: number};
}

// `Required<SimplifiedFeedbackGradesResponse>` doesn't work here: it's a shallow transformation,
// it only removes the `?` from `criteria_grades`/`level` themselves, without descending into the
// nested `Partial<...>` fields inside them — `criteriaGrades.c2` would still be typed as
// possibly `undefined` even after the guard. That's why the "complete" shape is declared
// explicitly above.
function isCompleteGradesPayload(
  payload: SimplifiedFeedbackGradesResponse,
): payload is CompleteGradesPayload {
  const {criteria_grades: criteriaGrades, level} = payload;

  return (
    criteriaGrades?.c2 != null &&
    criteriaGrades?.c4 != null &&
    criteriaGrades?.c6 != null &&
    level?.label != null &&
    level?.stars != null
  );
}

export const fetchSimplifiedFeedbackGradesService = (
  compositionId?: number | string,
): Promise<AxiosResponse<SimplifiedFeedbackData | undefined>> =>
  letrusAPI(
    `/api/v1/compositions/${compositionId}/simplified_feedback_grades/`,
  ).then((response: AxiosResponse<SimplifiedFeedbackGradesResponse>) => {
    if (!isCompleteGradesPayload(response.data)) {
      return {...response, data: undefined};
    }

    const {criteria_grades: criteriaGrades, level} = response.data;

    return {
      ...response,
      data: {
        criteriaGrades,
        level: {label: level.label, stars: level.stars},
      },
    };
  });
